import { perfilParaEstado, pontoDaAbertura } from '../caminhaoTanque/perfil'
import { PRODUTOS } from '../produtos'
import { deArray, girarEmTorno, paraArray, vetor } from '../geometria/vetor3'
import type { CenaTabletop, ObjetoTabletop, TipoObjetoTabletop, TransformacaoTabletop, VetorTabletop } from './tipos'

export const ehRiscoDeTanque = (tipo: TipoObjetoTabletop): boolean =>
  ['fogoTanque', 'vazamento', 'liquidoDerramado', 'bleve'].includes(tipo)

export function perfilDoObjetoTanque(objeto: ObjetoTabletop) {
  const produto = PRODUTOS.find(p => p.id === objeto.fonte?.idProduto)
  return perfilParaEstado(produto?.estadoTransporte ?? 'gasLiquefeitoPressurizado').tanque
}

/** Mesma ordem XYZ do Euler usado pelo Three: aplica Z, depois Y, depois X. */
export function girarVetorMesa(ponto: VetorTabletop, rotacao: VetorTabletop): VetorTabletop {
  let p = deArray([...ponto])
  p = girarEmTorno(p, vetor(0, 0, 1), rotacao[2])
  p = girarEmTorno(p, vetor(0, 1, 0), rotacao[1])
  p = girarEmTorno(p, vetor(1, 0, 0), rotacao[0])
  return paraArray(p)
}

export function pontoNoMundo(ponto: VetorTabletop, t: TransformacaoTabletop): VetorTabletop {
  const p = girarVetorMesa([ponto[0] * t.escala[0], ponto[1] * t.escala[1], ponto[2] * t.escala[2]], t.rotacao)
  return [p[0] + t.posicao[0], p[1] + t.posicao[1], p[2] + t.posicao[2]]
}

export function transformacaoDoRisco(tipo: TipoObjetoTabletop, tanque: ObjetoTabletop, escala: VetorTabletop = [1, 1, 1]): TransformacaoTabletop {
  const perfil = perfilDoObjetoTanque(tanque)
  const fogo = tipo === 'fogoTanque'
  const abertura = perfil.aberturas.find(a => fogo ? a.tipo === 'valvulaAlivio' || a.tipo === 'bocaVisita' : a.tipo === 'valvulaFaseLiquida' || a.tipo === 'valvulaFundo') ?? perfil.aberturas[0]!
  const ponto = tipo === 'bleve'
    ? [0, perfil.alturaEixoM, perfil.zCentroM] as const
    : pontoDaAbertura(perfil, abertura)
  // Terminação externa da peça desenhada em Abertura3D, não a captação interna.
  const saida: VetorTabletop = tipo === 'bleve' ? ponto
    : fogo && abertura.tipo === 'valvulaAlivio' ? [ponto[0], ponto[1] + 0.43, ponto[2]]
    : fogo ? [ponto[0], ponto[1] + 0.2, ponto[2]]
    : [ponto[0], ponto[1] - 0.25, ponto[2] + 0.2925]
  const mundo = pontoNoMundo(saida, tanque.transformacao)
  const direcao = girarVetorMesa([0, 0, 1], tanque.transformacao.rotacao)
  return {
    posicao: tipo === 'liquidoDerramado' ? [mundo[0] + direcao[0] * .9, 0, mundo[2] + direcao[2] * .9] : mundo,
    rotacao: fogo ? [...tanque.transformacao.rotacao] : [0, 0, 0],
    escala,
  }
}

export function encaixarRiscoNoTanque(cena: CenaTabletop, idRisco: string, idTanque: string | null): CenaTabletop {
  const risco = cena.objetos.find(o => o.id === idRisco)
  const tanque = cena.objetos.find(o => o.id === idTanque && o.tipo === 'caminhaoTanque')
  if (!risco || !ehRiscoDeTanque(risco.tipo) || (idTanque && !tanque)) throw new Error('Escolha um risco e um caminhão-tanque válidos.')
  return { ...cena, objetos: cena.objetos.map(o => {
    if (o.id !== idRisco) return o
    const { idTanque: anterior, ...livre } = o
    void anterior
    return tanque ? { ...livre, idTanque: tanque.id, transformacao: transformacaoDoRisco(o.tipo, tanque, o.transformacao.escala) } : livre
  }) }
}

export function atualizarRiscosVinculados(cena: CenaTabletop, idTanque: string): CenaTabletop {
  const tanque = cena.objetos.find(o => o.id === idTanque && o.tipo === 'caminhaoTanque')
  if (!tanque) return cena
  return { ...cena, objetos: cena.objetos.map(o => o.idTanque === idTanque
    ? { ...o, transformacao: transformacaoDoRisco(o.tipo, tanque, o.transformacao.escala) } : o) }
}

/** Distância à projeção do costado: não captura riscos colocados longe do caminhão. */
export function tanqueProximoDoPonto(cena: CenaTabletop, ponto: readonly [number, number]): string | null {
  let melhor: string | null = null
  let distancia = 2.5
  for (const tanque of cena.objetos) {
    if (tanque.tipo !== 'caminhaoTanque' || tanque.oculto) continue
    const perfil = perfilDoObjetoTanque(tanque)
    const a = pontoNoMundo([0, perfil.alturaEixoM, perfil.zCentroM - perfil.comprimentoM / 2], tanque.transformacao)
    const b = pontoNoMundo([0, perfil.alturaEixoM, perfil.zCentroM + perfil.comprimentoM / 2], tanque.transformacao)
    const dx = b[0] - a[0], dz = b[2] - a[2]
    const comprimento2 = dx * dx + dz * dz
    const t = comprimento2 ? Math.max(0, Math.min(1, ((ponto[0] - a[0]) * dx + (ponto[1] - a[2]) * dz) / comprimento2)) : 0
    const d = Math.max(0, Math.hypot(ponto[0] - a[0] - t * dx, ponto[1] - a[2] - t * dz) - perfil.larguraM / 2 * Math.max(...tanque.transformacao.escala))
    if (d < distancia) { distancia = d; melhor = tanque.id }
  }
  return melhor
}
